import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Globe, Check } from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const languages = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'ja', label: '日本語', flag: 'JA' },
  { code: 'zh', label: '中文', flag: 'ZH' },
]

export function LanguageSwitcher() {
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [isHighlighted, setIsHighlighted] = useState(false)

  const currentLang = lang || 'ja'
  const currentLanguage = languages.find((l) => l.code === currentLang) || languages[0]

  // Show tooltip on initial mount after a short delay
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    // Show tooltip after 1 second delay
    const showTimer = setTimeout(() => setShowTooltip(true), 1000)
    // Hide tooltip after 6 more seconds (7 total from mount)
    const hideTimer = setTimeout(() => setShowTooltip(false), 7000)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  useEffect(() => {
    const handleHighlight = () => {
      setIsHighlighted(true)
      // Also show tooltip when highlighted
      setShowTooltip(true)
      // Reset after animation completes
      setTimeout(() => {
        setIsHighlighted(false)
        setShowTooltip(false)
      }, 3000)
    }

    window.addEventListener('highlight-language-selector', handleHighlight)
    return () => window.removeEventListener('highlight-language-selector', handleHighlight)
  }, [])

  const handleLanguageChange = (newLang: string) => {
    // Replace the language in the current path
    const pathParts = location.pathname.split('/')
    pathParts[1] = newLang // The language is always at index 1 (/:lang/...)
    const newPath = pathParts.join('/')
    navigate(newPath)
  }

  return (
    <TooltipProvider>
      <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 transition-all duration-500 ease-in-out",
                  isHighlighted && "scale-125 brightness-125 ring-4 ring-cyan-400/50 shadow-[0_0_20px_rgba(34,211,238,0.6)] bg-white/30"
                )}
                aria-label="Change language"
              >
                <Globe className="h-4 w-4" />
                <span className="font-medium text-sm">{currentLanguage.label}</span>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            showArrow
            className="bg-black text-white px-3 py-2 rounded-lg shadow-lg [&>svg]:fill-black"
          >
            <p className="text-sm font-medium">English · 日本語 · 中文</p>
          </TooltipContent>
          <DropdownMenuContent align="end" className="w-40">
            {languages.map((language) => (
              <DropdownMenuItem
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <span className="font-medium text-xs">{language.flag}</span>
                <span className="text-sm flex-1">{language.label}</span>
                {currentLang === language.code && (
                  <Check className="h-4 w-4 text-blue-600" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  )
}
